import React, { useState, useMemo, useRef, useEffect } from "react";
import { FlightDetails, ConnectionFlight } from "./types";
import {
  fetchFlights,
  getFlightNumberHistory,
  getFlightNumbers,
  getLastFlightNumber,
} from "apis/loadsServices";
import SearchResults from "./SearchResults";
import { useGlobalLoader } from "contexts/GlobalLoaderContext";
import { useToastStore } from "store/toastStore";
import Select from "react-select";
import { Id, toast, ToastContainer } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDateTime } from "utils/dateUtils";

// Custom styles for DatePicker
const datePickerStyles = `
  .react-datepicker__day--highlighted {
    background-color: #FFD700 !important;
    color: #000 !important;
  }
  .react-datepicker__day--highlighted:hover {
    background-color: #FFC700 !important;
  }
`;

interface SearchFlightsNumberProps {
  onShowSearchResults?: (show: boolean) => void; // Add callback prop
  onPostRequestSuccess?: () => void; // Add callback for successful post requests
}

// localStorage utility functions for last flight search
const LAST_FLIGHT_SEARCH_STORAGE_KEY = "staffTraveler_lastFlightSearch";

interface LastFlightSearchData {
  flightNumber: string;
  dates: string[]; // Array of "YYYY-MM-DD" formatted dates
  savedAt: number; // Timestamp
}

const saveLastFlightSearchToStorage = (flightNumber: string, dates: string[]): void => {
  try {
    const data: LastFlightSearchData = {
      flightNumber,
      dates,
      savedAt: Date.now(),
    };
    localStorage.setItem(LAST_FLIGHT_SEARCH_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Error saving last flight search to localStorage:", error);
  }
};

const loadLastFlightSearchFromStorage = (): LastFlightSearchData | null => {
  try {
    const stored = localStorage.getItem(LAST_FLIGHT_SEARCH_STORAGE_KEY);
    if (!stored) return null;
    
    const data: LastFlightSearchData = JSON.parse(stored);
    // Validate data structure
    if (data.flightNumber && Array.isArray(data.dates)) {
      return data;
    }
    return null;
  } catch (error) {
    console.error("Error loading last flight search from localStorage:", error);
    return null;
  }
};


const SearchFlightsNumber: React.FC<SearchFlightsNumberProps> = ({
  onShowSearchResults,
  onPostRequestSuccess,
}) => {
  const [showResults, setShowResults] = useState<boolean>(false);
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [debouncedInputValue, setDebouncedInputValue] = useState<string>("");
  const [flightNumberOptions, setFlightNumberOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [date, setDate] = useState<string>(
    new Date(Date.now() + 86400000).toISOString().split("T")[0]
  ); // Default to Tomorrow's date
  const [flights, setFlights] = useState<FlightDetails[]>([]);
  const [connectionFlights, setConnectionFlights] = useState<ConnectionFlight[]>([]);
  // const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer">("none");
  const [stopsFilter, setStopsFilter] = useState<"none" | "one-or-fewer" | "one" | "two" | "three" | null>("none");
  const [isLoadingFlights, setIsLoadingFlights] = useState<boolean>(false);
  const [errors, setErrors] = useState<{
    flightNumber?: string;
    date?: string;
    apiResponse?: string;
  }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { showLoader, hideLoader } = useGlobalLoader();
  const dateInputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Track if we're auto-resetting to prevent infinite loops
  const isAutoResetting = useRef<boolean>(false);
  const [PageNumber, setPageNumber] = useState(1);
  const [totalFlights, setTotalFlights] = useState(0);
  const [range, setRange] = useState<[Date | null, Date | null]>([null, null]);
  const [datesInRange, setDatesInRange] = useState<string[]>([]);
  const [startDate, endDate] = range;
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const flightsPerPage = 9;
  const totalPages = Math.ceil(totalFlights / flightsPerPage);

  const handleFlightNumberChange = (selectedOption: any) => {
    if (selectedOption) {
      setFlightNumber(selectedOption.value);
      setErrors((prev) => ({
        ...prev,
        flightNumber: undefined,
        apiResponse: undefined,
      }));
      
      // Auto-open the date picker when flight number is selected
      setTimeout(() => {
        setIsCalendarOpen(true);
      }, 300);
    } else {
      setFlightNumber("");
      setInputValue("");
    }
  };

  // Debounce input value with 800ms delay
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  // Load flight number options when debounced input value changes
  useEffect(() => {
    if (debouncedInputValue !== undefined && debouncedInputValue.trim() !== "") {
      loadFlightNumberOptions(debouncedInputValue);
    }
  }, [debouncedInputValue]);

  const loadFlightNumberOptions = async (inputValue: string) => {
    setIsLoading(true);
    try {
      const response = await getFlightNumbers(inputValue);

      // Safely access response data with proper error handling
      let options: Array<{ value: string; label: string }> = [];

      // First, check if response exists and has data property
      if (response && Array.isArray(response.data)) {
        options = response.data
          .filter(
            (item: any): item is { flightNumber: string } =>
              item &&
              typeof item === "object" &&
              typeof item.flightNumber === "string"
          )
          .map((item: { flightNumber: string }) => ({
            value: item.flightNumber,
            label: item.flightNumber,
          }));
      }
      // If response.data isn't an array but response itself is the array
      else if (response && Array.isArray(response)) {
        options = response
          .filter(
            (item: any): item is { flightNumber: string } =>
              item &&
              typeof item === "object" &&
              typeof item.flightNumber === "string"
          )
          .map((item: { flightNumber: string }) => ({
            value: item.flightNumber,
            label: item.flightNumber,
          }));
      }
      // If we got a different response structure that includes the array elsewhere
      else if (response && typeof response === "object") {
        // Try to find an array of flight numbers somewhere in the response
        const responseValues = Object.values(response);
        for (const value of responseValues) {
          if (Array.isArray(value)) {
            const validItems = value.filter(
              (item: any): item is { flightNumber: string } =>
                item &&
                typeof item === "object" &&
                typeof item.flightNumber === "string"
            );

            options = validItems.map((item: { flightNumber: string }) => ({
              value: item.flightNumber,
              label: item.flightNumber,
            }));
            break;
          }
        }
      }

      setFlightNumberOptions(options);
      return options;
    } catch (error) {
      console.error("Error fetching flight number options:", error);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  // const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | null) => {
  const handleStopsFilterChange = (filter: "none" | "one-or-fewer" | "one" | "two" | "three" | null) => {
    if (!filter) return;
    setStopsFilter(filter);
    // If we have already searched, trigger a new search with the updated filter
    if (hasSearched) {
      getFlights({ stopsFilter: filter });
    }
  };

  const handleLastSearchClick = async () => {
    try {
      // First check localStorage
      let lastSearchData: LastFlightSearchData | null = loadLastFlightSearchFromStorage();
      
      // If not found in localStorage, fetch from API as fallback
      if (!lastSearchData) {
        showLoader(); // Show global loader for API call
        try {
          const { lastSearchFlightNumber } = await getLastFlightNumber();
          if (lastSearchFlightNumber) {
            // Convert UTC date to YYYY-MM-DD format
            const lastSearchDate = new Date(lastSearchFlightNumber.date)
              .toISOString()
              .split("T")[0];
            
            lastSearchData = {
              flightNumber: lastSearchFlightNumber.flightNumber,
              dates: [lastSearchDate],
              savedAt: Date.now(),
            };
            
            // Save to localStorage for future use
            saveLastFlightSearchToStorage(
              lastSearchFlightNumber.flightNumber,
              [lastSearchDate]
            );
          } else {
            console.log("No last search data available");
            hideLoader();
            return;
          }
        } catch (error) {
          console.error("Error fetching last search from API:", error);
          hideLoader();
          return;
        } finally {
          hideLoader();
        }
      }
      
      // Populate fields with stored data
      if (lastSearchData) {
        const { flightNumber, dates } = lastSearchData;
        
        setFlightNumber(flightNumber);
        
        // Convert date strings to Date objects for calendar
        const dateObjects = dates.map(dateStr => new Date(dateStr));
        setSelectedDates(dateObjects);
        setDatesInRange(dates);
        
        setErrors({}); // Clear any existing errors

        // Trigger search
        setHasSearched(true);
        setPageNumber(1);
        
        try {
          showLoader();
          await getFlights({ flightNumber, dates, pageNumber: 1 });
          
          // Scroll to search results
          setTimeout(() => {
            const searchResultsElement = document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for last search:", error);
        } finally {
          hideLoader();
        }
      }
    } catch (error) {
      console.error("Failed to load last search:", error);
    }
  };

  // Fetch flights when page number changes (but not on initial load)
  useEffect(() => {
    if (hasSearched && PageNumber > 1) {
      const fetchFlightsForPage = async () => {
        try {
          showLoader();
          await getFlights();

          // Scroll to search results after page change
          setTimeout(() => {
            const searchResultsElement =
              document.getElementById("search-results");
            if (searchResultsElement) {
              searchResultsElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching flights for page:", error);
        } finally {
          hideLoader();
        }
      };
      fetchFlightsForPage();
    }
  }, [PageNumber]);

  // Auto-reset filter to "none" when "one-or-fewer" returns no valid connection flights
  useEffect(() => {
    if (
      hasSearched &&
      stopsFilter === "one-or-fewer" &&
      !isLoadingFlights &&
      !isAutoResetting.current
    ) {
      // Check if there are any valid connection flights (must have at least 2 segments)
      const hasValidConnections = connectionFlights.some(
        (connection) => connection.segments && connection.segments.length >= 2
      );
      
      // If no valid connection flights and no direct flights, revert to non-stop
      if (!hasValidConnections && flights.length === 0) {
        // Prevent infinite loops by setting the flag
        isAutoResetting.current = true;
        // Reset filter to "none" and trigger a new search
        setStopsFilter("none");
        // Reset the flag after a short delay to allow the filter change to process
        setTimeout(() => {
          isAutoResetting.current = false;
        }, 100);
      }
    }
  }, [flights.length, connectionFlights, stopsFilter, hasSearched, isLoadingFlights]);

  const getFlights = async (overrideParams?: {
    // stopsFilter?: "none" | "one-or-fewer";
    stopsFilter?: "none" | "one-or-fewer" | "one" | "two" | "three" | null;
    flightNumber?: string;
    dates?: string[];
    pageNumber?: number;
  }) => {
    setIsLoadingFlights(true);
    try {
      const currentFilter = overrideParams?.stopsFilter ?? stopsFilter;
      let Stops: number | undefined;
      let IncludeDirect: boolean | undefined;
      let IncludeConnections: boolean | undefined;

      if (currentFilter === "none") {
        IncludeDirect = true;
        IncludeConnections = false;
        Stops = 0;
      } else if (currentFilter === "one-or-fewer") {
        IncludeDirect = false;
        IncludeConnections = true;
      } else if (currentFilter === "one") {
        Stops = 1;
      } else if (currentFilter === "two") {
        Stops = 2;
      } else if (currentFilter === "three") {
        Stops = 3;
      } else {
        // Default: non-stop only
        IncludeDirect = true;
        IncludeConnections = false;
      }

      const params = {
        FlightNumber: overrideParams?.flightNumber ?? flightNumber,
        Dates: overrideParams?.dates ?? datesInRange,
        PageNumber: overrideParams?.pageNumber ?? PageNumber,
        PageSize: flightsPerPage,
        IncludeDirect,
        IncludeConnections,
        ...(Stops !== undefined ? { Stops } : {}),
      };
      const response = await fetchFlights(params);
      const rawFlightData = response.data.items || [];
      const connectionsData = (response.data.connections || []) as ConnectionFlight[];

      // Check if we requested connections but got direct flights instead
      // This happens when API returns direct flights in items array when IncludeConnections: true
      if (currentFilter === "one-or-fewer" && connectionsData.length === 0 && rawFlightData.length > 0) {
        // Check if rawFlightData contains direct flights (stops: 0 or intermediateAirports: [])
        // The API response has 'stops' field in the raw data
        const hasDirectFlights = rawFlightData.some(
          (flight: any) => {
            return (flight.stops === 0 || flight.stops === undefined) ||
                   (flight.intermediateAirports && Array.isArray(flight.intermediateAirports) && flight.intermediateAirports.length === 0);
          }
        );
        
        // If we got direct flights instead of connections, we need to revert to non-stop
        if (hasDirectFlights && !isAutoResetting.current) {
          isAutoResetting.current = true;
          setStopsFilter("none");
          // Don't process this response, let the filter change trigger a new search
          setIsLoadingFlights(false);
          setTimeout(() => {
            isAutoResetting.current = false;
          }, 100);
          return;
        }
      }
      
      const flightData = rawFlightData as FlightDetails[];

      // Generate composite IDs for direct flights
      const flightsWithCompositeIds = flightData.map((flight) => {
        const baseId = flight.id ?? `${Date.now()}-${Math.random()}`;
        const scheduleDate = flight.scheduleDate || flight.date;
        const compositeId = scheduleDate
          ? `${baseId}_${scheduleDate.replace(/[^0-9]/g, "")}`
          : String(baseId);

        return {
          ...flight,
          compositeId,
        };
      });

      // Generate composite IDs for connection flights
      const connectionsWithCompositeIds = connectionsData.map((connection) => {
        // Generate composite IDs for each segment
        const segmentsWithIds = connection.segments.map((segment) => {
          const baseId = segment.id ?? `${Date.now()}-${Math.random()}`;
          const departureTime = segment.departureTime;
          const compositeId = departureTime
            ? `${baseId}_${departureTime.replace(/[^0-9]/g, "")}`
            : String(baseId);

          return {
            ...segment,
            compositeId,
          };
        });

        // Generate composite ID for the connection itself
        // Use the first segment's composite ID as part of the connection ID
        const firstSegmentId = segmentsWithIds[0]?.compositeId || "";
        const connectionCompositeId = `conn_${firstSegmentId}_${connection.totalDuration}`;

        return {
          ...connection,
          segments: segmentsWithIds,
          compositeId: connectionCompositeId,
        };
      });

      setFlights(flightsWithCompositeIds);
      setConnectionFlights(connectionsWithCompositeIds);

      // If API doesn't provide totalCount, estimate it based on current page and flight count
      if (response.data.total !== undefined) {
        setTotalFlights(response.data.total);
      } else {
        // Fallback: if we get less than flightsPerPage, we're on the last page
        const estimatedTotal =
          flightData.length < flightsPerPage
            ? (PageNumber - 1) * flightsPerPage + flightData.length
            : PageNumber * flightsPerPage; // This is a minimum estimate
        setTotalFlights(estimatedTotal);
      }

      setShowResults(flightData.length > 0 || connectionsData.length > 0);
    } catch (error: any) {
      const apiMessage = error.response?.data?.message;
      const id: Id = "flight-error";
      if (!toast.isActive(id)) {
        toast.error(apiMessage || "Failed to fetch flights.", { toastId: id });
      }
      setFlights([]);
      setConnectionFlights([]);
      setTotalFlights(0);
      setShowResults(false);
    } finally {
      setIsLoadingFlights(false);
    }
  };

  const handleSearchClick = async () => {
    setHasSearched(true);
    setPageNumber(1); // Reset to first page on new search
    const newErrors: typeof errors = {};
    if (!flightNumber) {
      newErrors.flightNumber = "*Flight number is required";
    }
    // if (!date) newErrors.date = "*Date is required";
    // setErrors(newErrors);

    if (!datesInRange) newErrors.date = "*Please select a date range.";

    if (Object.keys(newErrors).length === 0) {
      try {
        showLoader();
        await getFlights();
        
        // Save to localStorage after successful search
        saveLastFlightSearchToStorage(flightNumber, datesInRange);

        // Scroll to search results after a brief delay to ensure content is rendered
        setTimeout(() => {
          const searchResultsElement =
            document.getElementById("search-results");
          if (searchResultsElement) {
            searchResultsElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching flights:", error);
      } finally {
        hideLoader();
      }
    }
  };

  const handleClearSearch = () => {
    setFlightNumber("");
    setInputValue("");
    setDebouncedInputValue("");
    setSelectedDates([]);
    setDatesInRange([]);
    setRange([null, null]);
    setFlights([]);
    setConnectionFlights([]);
    setStopsFilter("none");
    setErrors({});
    setHasSearched(false);
    setPageNumber(1);
    setTotalFlights(0);
    onShowSearchResults?.(false);
  };

  const getDatesBetween = (start: Date, end: Date) => {
    const dates: string[] = [];
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split("T")[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const handleChange = (update: [Date | null, Date | null]) => {
    setRange(update);
    if (update[0] && update[1]) {
      setDatesInRange(getDatesBetween(update[0], update[1]));
    } else {
      setDatesInRange([]);
    }
  };

  const formatDateLocal = (date: Date) => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateStr = formatDateLocal(date);
      const dateIndex = selectedDates.findIndex(
        (d) => formatDateLocal(d) === dateStr
      );

      if (dateIndex > -1) {
        // Date already selected, remove it
        const newDates = selectedDates.filter(
          (_, index) => index !== dateIndex
        );
        setSelectedDates(newDates);
      } else {
        // Add new date
        const newDates = [...selectedDates, date].sort(
          (a, b) => a.getTime() - b.getTime()
        );
        setSelectedDates(newDates);
      }

      // Clear date error when a date is selected
      if (errors.date) {
        setErrors((prev) => ({ ...prev, date: undefined }));
      }
    }
  };

  const handleSaveDates = () => {
    setDatesInRange(selectedDates.map((d) => formatDateLocal(d)));
    setIsCalendarOpen(false);
  };

  const clearAllDates = () => {
    setSelectedDates([]);
    setDatesInRange([]);
  };

  const searchResults = useMemo(() => {
    if (!hasSearched) return null;
    // if (!isLoadingFlights && flights.length === 0 && connectionFlights.length === 0) {
    //   return (
    //     <p className="text-center text-gray-500 text-lg mt-6">No Data Found</p>
    //   );
    // }
    // onShowSearchResults?.(true);
    return (
      <div id="search-results">
        <SearchResults
          flights={flights}
          connectionFlights={connectionFlights}
          stopsFilter={stopsFilter}
          onStopsFilterChange={handleStopsFilterChange}
          isLoadingFlights={isLoadingFlights}
          show={true}
          searchDate={date}
          selectedDates={datesInRange}
          onPostRequestSuccess={onPostRequestSuccess}
        />
        {totalPages > 1 && (
          <div style={{ marginTop: 20, justifyItems: "center" }}>
            <p>
              Page: {PageNumber} of {totalPages}
            </p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              {[...Array(totalPages)]
                .map((_, i) => i + 1)
                .filter((page) => {
                  const start = Math.max(PageNumber - 2, 1);
                  const end = Math.min(PageNumber + 2, totalPages);
                  return page >= start && page <= end;
                })
                .map((page) => (
                  <button
                    key={page}
                    onClick={() => {
                      if (PageNumber !== page) {
                        setPageNumber(page);
                      }
                    }}
                    style={{
                      padding: "6px 12px",
                      backgroundColor:
                        PageNumber === page ? "#304cb2" : "#f0f0f0",
                      color: PageNumber === page ? "#fff" : "#000",
                      border: "none",
                      borderRadius: "4px",
                      cursor: PageNumber === page ? "default" : "pointer",
                      opacity: PageNumber === page ? 1 : 0.8,
                    }}
                    disabled={PageNumber === page}
                  >
                    {page}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [
    hasSearched,
    flights,
    connectionFlights,
    stopsFilter,
    isLoadingFlights,
    onShowSearchResults,
    PageNumber,
    totalPages,
    totalFlights,
    date,
    datesInRange,
    onPostRequestSuccess,
  ]);

  // Load initial flight number options
  // useEffect(() => {
  //   // Try to load initial options
  //   try {
  //     loadFlightNumberOptions("");
  //   } catch (err) {
  //     console.error("Failed to load initial flight numbers:", err);
  //   }
  // }, []);

  return (
    <div className="mt-0 bg-[245,245,245] px-4">
      <style>{datePickerStyles}</style>
      <ToastContainer />
      <div className="bg-white rounded-lg rounded-tl-none p-4 px-4 ">
        <div className="flex items-center justify-between pb-4 border-b border-lightgray-50">
          <p className="text-grease-100 text-sm font-medium">
            Search by Flight Number
          </p>
          <div className="flex gap-2">
            <button
              className="flex gap-2 p-3 border border-[#FFBC00] rounded-lg"
              onClick={handleClearSearch}
              type="button"
            >
              <i className="st-close text-[#FFBC00]"></i>
              <p className="font-semibold text-[#FFBC00] text-xs">Clear</p>
            </button>
            <button
              className="flex gap-2 p-3 border border-[#FFBC00] rounded-lg"
              onClick={handleLastSearchClick}
              type="button"
            >
              <i className="st-previous text-[#FFBC00]" />
              <p className="font-semibold text-[#FFBC00] text-xs">
                Last Search
              </p>
            </button>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-6 my-4">
          <div>
            <label className="block mb-2 text-sm text-lightgray-200">
              Flight Number
            </label>
            <Select
              className="basic-single"
              classNamePrefix="select"
              value={
                flightNumber
                  ? { value: flightNumber, label: flightNumber }
                  : null
              }
              isLoading={isLoading}
              isClearable={true}
              isSearchable={true}
              name="flightNumber"
              options={flightNumberOptions}
              onChange={handleFlightNumberChange}
              onInputChange={(inputValue) => {
                setInputValue(inputValue);
              }}
              onFocus={() => {
                // Only load options when the field is focused if there are no options already
                if (flightNumberOptions.length === 0) {
                  loadFlightNumberOptions("");
                }
              }}
              placeholder="Select or search flight number"
              styles={{
                control: (baseStyles) => ({
                  ...baseStyles,
                  backgroundColor: "#F0F7FF", // bg-lightblue-100
                  border: "none",
                  padding: "0.5rem",
                  borderRadius: "0.5rem",
                }),
                option: (baseStyles, state) => ({
                  ...baseStyles,
                  backgroundColor: state.isSelected
                    ? "#2563EB"
                    : state.isFocused
                      ? "#DBEAFE"
                      : undefined,
                  color: state.isSelected ? "white" : undefined,
                }),
              }}
            />
            {errors.flightNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.flightNumber}</p>
            )}
          </div>
          {/* <div>
            <label className="block mb-2 text-sm text-lightgray-200">
              Date
            </label>
            <div className="relative">
              <input
                ref={dateInputRef}
                type="date"
                className="block w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
                value={date}
                min={new Date().toISOString().split("T")[0]}

                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const typed = e.target.value;
                  const [year, month, day] = typed.split("-");

                  if (year.length > 4) {
                    return;
                  }

                  setDate(typed);
                  if (errors.date) {
                    setErrors(prev => ({ ...prev, date: undefined }));
                  }
                }}
              />
              <button
                className="absolute inset-y-0 end-0 flex items-center z-20 px-3 cursor-pointer text-gray-400 rounded-e-md focus:outline-none"
                onClick={() => {
                  if (dateInputRef.current) {
                    dateInputRef.current.showPicker();
                  }
                }}
                type="button"
              >
                <i className="st-calendar text-lightgray-200"></i>
              </button>
            </div>

          </div> */}

          <div className="flex flex-col">
            <label className="block mb-2 text-sm text-lightgray-200">
              Select Dates
            </label>
            {/* <DatePicker
              selectsRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleChange}
              isClearable
              minDate={new Date()}
              placeholderText="Select a date range"
              className="block w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 placeholder-gray-400 focus:outline-none"
            /> */}
            <div className="relative">
              <DatePicker
                selected={null}
                onChange={handleDateChange}
                onCalendarClose={() => setIsCalendarOpen(false)}
                onCalendarOpen={() => setIsCalendarOpen(true)}
                open={isCalendarOpen}
                onClickOutside={() => setIsCalendarOpen(false)}
                shouldCloseOnSelect={false}
                minDate={new Date()}
                placeholderText={`${datesInRange.length > 0 ? `Selected Dates (${datesInRange.length})` : "Select travel dates"}`}
                className={`block w-full p-4 pr-10 border-none rounded-lg bg-lightblue-100 text-sm text-grease-100 ${datesInRange.length > 0 ? "placeholder-black" : "placeholder-gray-400"} focus:outline-none cursor-pointer`}
                highlightDates={selectedDates}
                inline={false}
                // calendarClassName="text-base [&_.react-datepicker\_\_day]:w-10 [&_.react-datepicker\_\_day]:h-8 [&_.react-datepicker\_\_day]:leading-10 [&_.react-datepicker\_\_day-name]:w-10 [&_.react-datepicker\_\_day-name]:h-10 [&_.react-datepicker\_\_day-name]:leading-10 [&_.react-datepicker\_\_current-month]:text-lg [&_.react-datepicker\_\_current-month]:mb-2"
                wrapperClassName="w-full"
                onInputClick={() => setIsCalendarOpen(true)}
              >
                <div className="flex justify-between w-full px-2 pt-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      clearAllDates();
                    }}
                    className="px-4 py-2 text-sm text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
                    type="button"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleSaveDates();
                    }}
                    className="px-4 py-2 text-sm text-white bg-[#FFBC00] rounded-lg hover:bg-[#e6a800]"
                    type="button"
                  >
                    Save ({selectedDates.length})
                  </button>
                </div>
              </DatePicker>
              {datesInRange.length > 0 && (
                <button
                  onClick={() => setIsCalendarOpen(true)}
                  className="absolute inset-y-0 end-0 flex items-center z-20 px-3  text-xl cursor-pointer text-gray-400 hover:text-blue-600 rounded-e-md focus:outline-none"
                  type="button"
                  title="Edit dates"
                >
                  <i className="st-calendar text-lightgray-200"></i>
                </button>
              )}
            </div>

            {errors.date && (
              <p className="text-red-500 text-sm mt-1">{errors.date}</p>
            )}
          </div>

          {/* Empty div to maintain original layout */}
          <div></div>
        </div>

        <div className="text-end">
          <button
            className="p-3 w-64 rounded-lg text-white text-center bg-[#FFBC00] text-sm tracking-wide"
            onClick={handleSearchClick}
          >
            SEARCH
          </button>
        </div>
      </div>
      {searchResults}
    </div>
  );
};

export default SearchFlightsNumber;
